// ============================================================================
// ORGANISATION VIEW MODAL — read-only detail view driven by a definition
// ============================================================================

import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import type { OrganisationRow, OrganisationScreenDefinition } from './organisationScreen.types';

interface OrganisationViewModalProps<TRecord extends OrganisationRow, TForm extends object> {
  definition: OrganisationScreenDefinition<TRecord, TForm>;
  isOpen: boolean;
  record: TRecord | null;
  onClose: () => void;
  onEdit?: (record: TRecord) => void;
  canEdit: boolean;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (recordCode: string) => void;
}

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.75rem',
    background: 'var(--bg-secondary)', borderLeft: '3px solid var(--primary)',
    borderRadius: '0 0.25rem 0.25rem 0', marginBottom: '0.75rem',
    fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)',
  }}>
    {icon}
    {title}
  </div>
);

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
    <div style={{ width: '160px', flexShrink: 0, fontWeight: 500, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
      {label}
    </div>
    <div style={{
      flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', background: 'var(--bg-secondary)',
      border: '1px solid var(--form-border-color)', borderRadius: '0.375rem', padding: '0.375rem 0.625rem',
      minHeight: '2rem', display: 'flex', alignItems: 'center',
    }}>
      {value === null || value === undefined || value === '' ? '-' : value}
    </div>
  </div>
);

export function OrganisationViewModal<TRecord extends OrganisationRow, TForm extends object>({
  definition, isOpen, record, onClose, onEdit, canEdit, tabId, moduleName, onRestoreView,
}: OrganisationViewModalProps<TRecord, TForm>): React.ReactElement | null {
  const { minimize } = useModalMinimize();
  const { noun, recordCode, recordName, viewSections } = definition;

  const handleMinimize = useCallback(() => {
    if (!tabId || !record) return;
    const code = recordCode(record);
    minimize({
      id: `${tabId}-view-modal-${code}`,
      title: `View - ${code}`,
      moduleName: moduleName || definition.moduleName,
      tabId,
      onRestore: () => onRestoreView?.(code),
    });
    onClose();
  }, [tabId, record, recordCode, minimize, moduleName, definition.moduleName, onClose, onRestoreView]);

  if (!isOpen || !record) return null;

  const sections = viewSections(record);

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        <div className="modal-header">
          <h2 className="modal-title">
            <IconSystem.eye size={20} />
            {noun}: {recordCode(record)} - {recordName(record)}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" type="button">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose} type="button">
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          {sections.map((section) => {
            const Icon = (IconSystem as any)[section.icon] || IconSystem.info;
            return (
              <div key={section.title} style={{ marginBottom: '1.5rem' }}>
                <SectionHeader icon={<Icon size={16} />} title={section.title} />
                {section.rows.map((row) => <DetailRow key={row.label} label={row.label} value={row.value} />)}
              </div>
            );
          })}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} type="button">Close</button>
          {canEdit && onEdit && (
            <button className="btn btn-primary" onClick={() => onEdit(record)} type="button">
              <IconSystem.edit size={16} /> Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default OrganisationViewModal;
